import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { getChhattisgarhDemandMetrics } from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { formatDateForAPI } from "../Utility/const";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip from "../../../components/Metrics";
import { DiscomLoadMetrics } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];
const GROUP_BY_OPTIONS = ["Discom Load", "Total Discom Load", "CSPDCL Drawal"];
const DISCOM_OPTIONS = ["CSPDCL"];
const TYPE_OPTIONS = ["Actual Load"];
interface ChhattisgarhSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  selectedSuffix: any[];
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: any[];
  metrics: DiscomLoadMetrics | null;
}

export function ChhattisgarhSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: ChhattisgarhSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchSummaryData = useCallback(async () => {
    setLoading(true);
    try {
      const dates = DefaultDates[activeTab]?.defaultRangeDate;
      if (!dates?.startDate || !dates?.endDate) return;

      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const raw = await getChhattisgarhDemandMetrics(startDateStr, endDateStr);
      setSummaryData(Array.isArray(raw) ? raw : []);
    } catch (error) {
      console.error("Error fetching Chhattisgarh summary data:", error);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // ─── Chart data derivation ────────────────────────────────────────────────
  useEffect(() => {
    const factor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const processed = summaryData.map((item) => ({
      slot: item.slot,
      slot_number: item.slot,
      slot_with_time:
        item.slot && item.time_string
          ? `${item.slot} (${item.time_string})`
          : item.slot,
      min_demand: (item.min_demand ?? 0) * factor,
      max_demand: (item.max_demand ?? 0) * factor,
      avg_demand: (item.avg_demand ?? 0) * factor,
      mode_demand: (item.mode_demand ?? 0) * factor,
      min_demand_date: item.date_of_min,
      max_demand_date: item.date_of_max,
    }));

    processed.sort((a, b) => a.slot - b.slot);

    const filtered =
      selectedBlock.length > 0
        ? processed.filter((item) =>
            selectedBlock.map((b) => b.value).includes(item.slot),
          )
        : processed;

    setChartData(filtered);
    setTableData(filtered);
  }, [summaryData, selectedUnit, selectedBlock]);

  // ─── Legends ──────────────────────────────────────────────────────────────
  const legends = useMemo(
    () => [
      {
        dataKey: "min_demand",
        name: "Min Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "min_demand_date",
      },
      {
        dataKey: "max_demand",
        name: "Max Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "max_demand_date",
      },
      {
        dataKey: "avg_demand",
        name: "Average Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
      },
      {
        dataKey: "mode_demand",
        name: "Mode Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
      },
    ],
    [selectedChartType],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  // ─── Tooltip ──────────────────────────────────────────────────────────────
  const customTooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      const items = Array.isArray(params) ? params : [params];
      const title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      const dataPoint = chartData[param.dataIndex];

      const tooltipItems = [...items]
        .sort((a, b) => (a.seriesName ?? "").localeCompare(b.seriesName ?? ""))
        .map((item) => {
          const value = parseFloat(item.value)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

          let displayName = item.seriesName;
          if (item.seriesName === "Min Demand" && dataPoint?.min_demand_date) {
            displayName = `Min Demand (${dataPoint.min_demand_date})`;
          } else if (
            item.seriesName === "Max Demand" &&
            dataPoint?.max_demand_date
          ) {
            displayName = `Max Demand (${dataPoint.max_demand_date})`;
          }

          return `
          <div class="tooltip-item">
            <div class="color-indicator" style="background-color:${item.color}"></div>
            <div class="series-name">${displayName}</div>
            <div class="value">${value} ${selectedUnit}</div>
          </div>`;
        });

      return `
      <div class="enhanced-tooltip">
        <div class="tooltip-header">
          <div class="tooltip-title">${title}</div>
        </div>
        <div class="tooltip-content">${tooltipItems.join("")}</div>
      </div>
      <style>
        .enhanced-tooltip { font-family: 'Inter', system-ui, sans-serif; min-width: 200px; }
        .tooltip-header { border-bottom: 1px solid #eaeaea; padding-bottom: 8px; margin-bottom: 8px; }
        .tooltip-title { font-weight: 600; font-size: 13px; color: #333; }
        .tooltip-content { max-height: 300px; overflow-y: auto; }
        .tooltip-item { display: flex; align-items: center; gap: 8px; padding: 4px 0; border-bottom: 1px solid #f0f0f0; }
        .tooltip-item:last-child { border-bottom: none; }
        .color-indicator { width: 10px; height: 10px; flex-shrink: 0; border-radius: 50%; }
        .series-name { font-weight: 500; font-size: 12px; color: #333; flex: 1; }
        .value { font-weight: 600; font-size: 12px; color: #333; }
      </style>`;
    },
    [chartData, selectedUnit],
  );

  // ─── Table props ──────────────────────────────────────────────────────────
  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: ["slot_number", "Date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Discom Summary",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />

          {/* <FilterMenu
            title="Discom"
            items={DISCOM_OPTIONS}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={onDiscomChange}
            // disabled={true}
          /> */}

          {/* <FilterMenu
            title="Group By"
            items={GROUP_BY_OPTIONS}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => onGroupByChange(index)}
            disabled={true}
          /> */}

          <FilterMenu
            title="Type"
            items={TYPE_OPTIONS}
            selectedItems={selectedSuffix.map((s) => s.displayName)}
            setSelectedItems={onTypeChange}
            // disabled={true}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(H_CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChip
              icon={<PeakIcon width={20} height={19} />}
              items={[
                {
                  label: "Peak Load",
                  value: metrics["Peak Load (MW)"].value,
                  unit: metrics["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<AvgIcon width={20} height={19} />}
              items={[
                {
                  label: "Average Load",
                  value: metrics["Average Load (MW)"].value,
                  unit: metrics["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<MinIcon width={20} height={19} />}
              items={[
                {
                  label: "Minimum Load",
                  value: metrics["Minimum Load (MW)"].value,
                  unit: metrics["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChip
              icon={<TotalIcon width={20} height={19} />}
              items={[
                {
                  label: "Total Energy",
                  value: metrics["Total Energy (MU)"].value,
                  unit: metrics["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Discom Summary"
              subtitle="Fetching Chhattisgarh discom summary data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Discom Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(tablePar);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else if (dataTab.label === "Table")
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={undefined}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  customTooltip={customTooltipFormatter}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
            1. Actual Load data is sourced from CSPDCL's official records."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
